import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import type { SubmittedOrder } from '@mypoz/shared';
import {
  gramsToKg,
  isOrderEditableFor,
  orderPayableCents,
  orderServiceNames,
  receiptImageUrl,
  totalFeeCents,
} from '@mypoz/shared';
import { useTranslation } from '@mypoz/i18n';

import { actionLinkClass, buttonClass } from '@/lib/styles.js';
import { editOrderRoute } from '@/shell/navigation.js';
import { useCopyToClipboard } from '@/lib/clipboard.js';
import { useFormat } from '@/lib/format.js';
import { legacyAssetOrigin } from '@/lib/legacy-api.js';
import { useOrderServices } from '@/features/orders/orderServicesContext.js';
import { useOrders } from '@/features/orders/context.js';

export interface OrderDetailPanelProps {
  order: SubmittedOrder;
  /** Carrier tracking is MYP-52; until then Track reports as unconnected. */
  onUnavailable: () => void;
}

/**
 * A section, with an optional Edit that deep-links into the step owning it.
 *
 * `editTo` rather than a callback: Edit here is a navigation into the edit flow, and passing the
 * destination keeps that visible at the call site instead of hiding it behind a handler.
 */
function Section({
  title,
  editTo,
  editLabel,
  children,
}: {
  title: string;
  editTo?: string;
  editLabel?: string;
  children: ReactNode;
}) {
  return (
    <section className="min-w-0">
      <div className="flex items-baseline justify-between gap-3">
        <h3 className="text-xs font-semibold tracking-[0.08em] text-muted uppercase">{title}</h3>
        {editTo && editLabel ? (
          <Link to={editTo} className={actionLinkClass('shrink-0 text-sm')}>
            {editLabel}
          </Link>
        ) : null}
      </div>
      <div className="mt-2.5 grid gap-1.5 text-sm">{children}</div>
    </section>
  );
}

/** A label on the left, its value hard against the right — used by fees and by add-ons. */
function Row({ label, value, strong }: { label: string; value: string; strong?: boolean }) {
  return (
    <div className="flex items-baseline justify-between gap-4">
      <span className={strong ? 'shrink-0 font-semibold text-ink' : 'shrink-0 text-muted'}>
        {label}
      </span>
      <span
        className={
          strong ? 'font-semibold text-ink tabular-nums' : 'text-right text-ink tabular-nums'
        }
      >
        {value}
      </span>
    </div>
  );
}

/**
 * Everything about an order that does not fit on its collapsed row.
 *
 * Six sections in one grid rather than a nested set of cards: this already sits inside a card, and
 * boxing each section again would make a busy panel out of what is mostly label-and-value pairs.
 */
export function OrderDetailPanel({ order, onUnavailable }: OrderDetailPanelProps) {
  const { t } = useTranslation();
  const fmt = useFormat();
  const { copied, copy } = useCopyToClipboard();
  const { detailStatus, loadDetail } = useOrders();
  const { services: catalogServices } = useOrderServices();

  /*
    Three of the six sections below — packages, address, timeline — exist only on the detail
    record, which is fetched when this panel opens (MYP-53). Until it lands they would each render
    their own empty state, and three separate "None"s read as an order with nothing in it rather
    than one still loading. One line above them says what is actually happening.
  */
  const detail = detailStatus(order.orderNumber);

  /*
    The bill, and what it does not itemise. `orderPayableCents` returns the vendor's own total,
    which on a good number of orders exceeds its own fee lines; the difference is real money and
    gets its own row rather than being quietly absorbed into either end of the breakdown.

    The sign is not decoration. Over the test account's 129 priced orders the residual came out
    positive on 44 and exactly zero on 85, and never negative — but "never seen" is not "cannot
    happen", and the itemised lines legitimately contain negative entries (ROUND ADJUSTMENT,
    Discount), so a schedule that overshoots the bill is a shape this data can take. Labelled
    "Other charges" it would then read as a charge that reduces the total, which is nonsense. So
    the label follows the sign and the arithmetic stays the same either way.
  */
  const lines = order.feeLines ?? [];
  const billed = orderPayableCents(order) ?? totalFeeCents(lines);
  const residual = billed - totalFeeCents(lines);

  const editLabel = t('orders.detail.edit');
  // Named from the catalog, else from the order itself — a retired service (live: 7 and 8) is
  // still on the order and must not vanish from its detail (MYP-89).
  const services = orderServiceNames(order.serviceIds, catalogServices, order.orderedServices);

  // No Edit link at all once the order has moved past editing — a link to a screen that will only
  // tell you it is locked is worse than no link.
  const editable = isOrderEditableFor(order);
  const editTo = (step: string) => (editable ? editOrderRoute(order.orderNumber, step) : undefined);

  return (
    <div className="grid gap-6 border-t border-line px-5 py-5 sm:px-6 md:grid-cols-2 xl:grid-cols-4">
      {detail === 'loading' ? (
        <p className="text-sm text-muted md:col-span-2 xl:col-span-4">
          {t('orders.detail.loading')}
        </p>
      ) : detail === 'error' ? (
        <div className="flex flex-wrap items-center gap-3 md:col-span-2 xl:col-span-4">
          <p className="text-sm text-ink">{t('orders.detail.loadFailed')}</p>
          <button
            type="button"
            onClick={() => loadDetail(order.orderNumber)}
            className={buttonClass('secondary', 'px-4 py-1.5 text-sm')}
          >
            {t('orders.detail.retry')}
          </button>
        </div>
      ) : null}

      <Section
        title={t('orders.detail.packages')}
        editTo={editTo('packages')}
        editLabel={editLabel}
      >
        {/*
          A cancelled order comes back with its `pkgInfo` row intact but every package column null
          — the vendor stops reporting the parcels once the order lets go of them. Without a line
          here that renders as a heading over nothing, which reads as a panel that failed to load
          rather than an order with no parcels on it.
        */}
        {order.packages.length === 0 ? (
          <p className="text-muted">{t('orders.detail.noPackages')}</p>
        ) : null}
        {order.packages.map((item) => (
          <div key={item.trackingNumber}>
            <p className="text-ink">
              <span className="font-semibold">{item.trackingNumber}</span>
              <span className="text-muted">
                {' · '}
                {item.itemName}
                {item.weightGrams === undefined
                  ? ''
                  : ` · ${t('orders.newOrder.weightKg', {
                      value: fmt.number(gramsToKg(item.weightGrams), 1),
                    })}`}
              </span>
            </p>
            {/*
              The receipts the vendor kept for this parcel (MYP-96), read back from
              `pkgInfo[].imageurl`. They open in a new tab rather than inline: they are photos of
              paper receipts, and the panel is too narrow to make one readable.
            */}
            {item.receiptPaths && item.receiptPaths.length > 0 ? (
              <p className="mt-0.5 flex flex-wrap items-center gap-x-2 text-xs">
                <span className="text-muted">{t('orders.detail.receipts')}</span>
                {item.receiptPaths.map((path, index) => (
                  <a
                    key={path}
                    href={receiptImageUrl(path, legacyAssetOrigin)}
                    target="_blank"
                    rel="noreferrer"
                    className={actionLinkClass()}
                  >
                    {t('orders.detail.receiptLink', { number: index + 1 })}
                  </a>
                ))}
              </p>
            ) : null}
          </div>
        ))}
      </Section>

      <Section title={t('orders.detail.fees')}>
        {order.feeLines && order.feeLines.length > 0 ? (
          <>
            {order.feeLines.map((line) => (
              <Row
                key={line.label}
                label={line.label}
                value={fmt.money(line.amountCents, line.currency ?? order.currency)}
              />
            ))}
            {/*
              What the vendor bills beyond the lines it itemises: add-on services on a summary row,
              and a delivery or pickup charge it sends as a bare amount with no name anywhere in
              the response. Shown as one honest line rather than spread over invented labels, and
              omitted entirely when the breakdown already adds up — which is most orders.
            */}
            {residual === 0 ? null : (
              <Row
                label={
                  residual > 0
                    ? t('orders.detail.otherCharges')
                    : t('orders.detail.otherAdjustment')
                }
                value={fmt.money(residual, order.currency)}
              />
            )}
            <div className="mt-1 border-t border-line pt-1.5">
              <Row
                label={t('orders.detail.total')}
                value={fmt.money(billed, order.currency)}
                strong
              />
            </div>
          </>
        ) : (
          <p className="text-muted">{t('orders.card.pricePending')}</p>
        )}
      </Section>

      <Section title={t('orders.detail.address')} editTo={editTo('delivery')} editLabel={editLabel}>
        {order.address ? (
          <>
            {order.address.recipient ? (
              <p className="text-ink">
                {[order.address.recipient, order.address.phone].filter(Boolean).join(' · ')}
              </p>
            ) : null}
            {order.address.line1 ? <p className="text-muted">{order.address.line1}</p> : null}
            <p className="text-muted">
              {[
                [order.address.postcode, order.address.city].filter(Boolean).join(' '),
                order.address.province,
                order.address.country,
              ]
                .filter(Boolean)
                .join(', ')}
            </p>
          </>
        ) : (
          <p className="text-muted">{t('orders.detail.none')}</p>
        )}
      </Section>

      <Section title={t('orders.detail.addOns')} editTo={editTo('delivery')} editLabel={editLabel}>
        <Row
          label={t('orders.detail.paymentMethod')}
          value={
            order.paymentMode === 'MANUAL'
              ? t('orders.detail.paymentManual')
              : t('orders.detail.paymentAuto')
          }
        />
        <Row
          label={t('orders.detail.services')}
          value={
            services.length === 0
              ? t('orders.detail.none')
              : services.map((service) => service.name).join(', ')
          }
        />
        <Row
          label={t('orders.detail.promoCode')}
          value={order.couponCode || t('orders.detail.none')}
        />
        <Row label={t('orders.detail.remark')} value={order.remark || t('orders.detail.none')} />
      </Section>

      {order.tracking ? (
        <Section title={t('orders.detail.tracking')}>
          <p className="text-muted">{t('orders.detail.trackingNumber')}</p>
          <p className="text-base font-semibold text-ink tabular-nums">
            {order.tracking.trackingNumber}
          </p>
          {/*
            Both optional since MYP-53, and omitted rather than filled with a dash. The vendor
            publishes no carrier scan feed (MYP-52), so a "Latest update: —" row on every
            despatched order is a column of dashes that teaches the customer to stop reading this
            section.
          */}
          {order.tracking.carrier ? (
            <Row label={t('orders.detail.carrier')} value={order.tracking.carrier} />
          ) : null}
          {order.tracking.latestUpdate ? (
            <Row label={t('orders.detail.latestUpdate')} value={order.tracking.latestUpdate} />
          ) : null}

          <div className="mt-1.5 flex flex-wrap items-center gap-3">
            <button
              type="button"
              onClick={() => void copy(order.tracking?.trackingNumber ?? '')}
              className={actionLinkClass()}
            >
              {copied ? t('warehouses.copied') : t('orders.detail.copyNumber')}
            </button>
            {/*
              Padding and type size make this smaller than a page-level button; height is left to
              buttonClass. cn() is twMerge, so a min-h passed here would delete its min-h-11
              rather than sit beside it — quietly costing the 44px touch target on phones while
              looking, in the class string, like a harmless tweak.
            */}
            <button
              type="button"
              onClick={onUnavailable}
              className={buttonClass('secondary', 'px-4 py-1.5 text-sm')}
            >
              {t('orders.detail.track')}
            </button>
          </div>
        </Section>
      ) : order.status === 'collecting' ? (
        /*
          Nothing to show yet rather than nothing shown. A collecting order has no waybill until
          the warehouse hands it to a carrier, and omitting the section entirely reads as a screen
          that forgot about tracking rather than an order that hasn't reached it — MYP-52. A
          cancelled order gets neither this nor the block above: it left the track for good, and
          "arrives once paid" would be a promise this order will never keep.
        */
        <Section title={t('orders.detail.tracking')}>
          <p className="text-muted">{t('orders.detail.trackingPending')}</p>
        </Section>
      ) : null}

      {order.timeline && order.timeline.length > 0 ? (
        <Section title={t('orders.detail.timeline')}>
          {/* Keyed on event *and* date: nothing stops a carrier reporting the same event twice. */}
          {order.timeline.map((entry) => (
            <Row
              key={`${entry.event}-${entry.at}`}
              label={t(`orders.detail.event.${entry.event}`)}
              value={fmt.date(entry.at)}
            />
          ))}
        </Section>
      ) : null}
    </div>
  );
}
